import React, { useEffect, useState, useCallback } from "react";
import { motion } from "framer-motion";
import { Sparkle, Lock, ArrowsClockwise } from "@phosphor-icons/react";
import { Link } from "react-router-dom";
import { api } from "../../lib/api";
import StockList from "./StockList";

/**
 * Golden Cross + Breakout scanner — fresh 50/200 EMA golden crosses on
 * NIFTY 500 stocks that are simultaneously breaking out above their last
 * 50-day high with above-average volume. Refreshed twice daily by the
 * scheduler. Free users see 2-row preview; premium gets the full list.
 */
export default function GoldenCrossView({ onWatch, watchedSet, onOpenStock }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setErr("");
    try {
      const { data } = await api.get("/golden-cross");
      setData(data);
    } catch (e) {
      console.error("/golden-cross", e);
      setErr(e?.response?.data?.detail || "Failed to load Golden Crosses");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const items = data?.items || [];
  const isPremium = data?.is_premium_view;

  return (
    <div data-testid="golden-cross-view">
      <div className="mb-4 flex items-start justify-between flex-wrap gap-3 border-b border-subtle pb-3">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Sparkle size={14} weight="fill" className="text-amber-400" />
            <div className="font-mono text-xs uppercase tracking-[0.25em] text-primary-fg">
              /// Golden Cross + Breakout
            </div>
          </div>
          <p className="text-xs text-muted-fg leading-relaxed max-w-2xl">
            Stocks jisme 50-EMA recently UP through 200-EMA cross kar gaya
            (last 30 trading days) — Close &gt; EMA50 · liquidity ≥ ₹5 Cr.
            Breakout flag + volume multiple shown as confirmation signals.
            NIFTY 500 universe · sorted by freshness · refreshed twice daily.
          </p>
        </div>
        <button
          type="button"
          onClick={load}
          disabled={loading}
          data-testid="golden-cross-reload"
          className="px-3 py-1.5 border border-subtle rounded-sm text-secondary-fg hover:bg-surface-hover text-[10px] font-mono uppercase tracking-widest flex items-center gap-1.5 disabled:opacity-50"
        >
          <ArrowsClockwise size={12} className={loading ? "animate-spin" : ""} weight="bold" />
          Refresh
        </button>
      </div>

      {err && (
        <div className="p-3 mb-3 border border-neg-soft bg-neg-soft text-neg text-sm rounded-sm" data-testid="golden-cross-error">
          {err}
        </div>
      )}

      <StockList
        items={items}
        loading={loading}
        onOpen={(it) => onOpenStock?.({ ...it, golden_cross_mode: true })}
        onWatch={onWatch}
        watchedSet={watchedSet}
        testIdPrefix="golden-cross-row"
        testid="golden-cross-list"
        emptyMessage="No fresh Golden Cross + breakout setups right now. Scanner refreshes twice daily during market hours."
        rowProps={(it) => ({
          typeBadge: {
            label: it.crossover_days_ago === 0 ? "TODAY" : `${it.crossover_days_ago}D AGO`,
            tone: it.crossover_days_ago === 0 ? "pos" : "mod",
          },
          rightHint: `+${it.ema_gap_pct}% gap · ${it.volume_multiple}× vol`,
        })}
      />

      {!isPremium && data && data.total > items.length && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="mt-6 border border-pos/40 rounded-sm p-5 bg-pos-soft/40 flex items-center justify-between gap-4 flex-wrap"
          data-testid="golden-cross-upgrade"
        >
          <div className="flex items-center gap-3">
            <Lock size={20} className="text-pos shrink-0" />
            <div>
              <div className="font-medium text-primary-fg">
                Showing 2 preview · {data.total - items.length} more locked
              </div>
              <div className="text-sm text-secondary-fg">
                Unlock every Golden Cross + breakout pick with a ₹5 trial.
              </div>
            </div>
          </div>
          <Link
            to="/pricing"
            className="px-5 py-2.5 rounded-sm text-sm font-medium"
            style={{ background: "hsl(var(--pos))", color: "hsl(var(--bg))" }}
          >
            Start ₹5 trial →
          </Link>
        </motion.div>
      )}
    </div>
  );
}
